import axios, { type AxiosInstance } from 'axios';

/**
 * NextPay Reader — API client (Sprint 1, 2026-05-07)
 *
 * Cliente axios pré-configurado para o backend NextPay.
 * - Auth via Bearer token (JWT) salvo em localStorage
 * - Os endpoints chamados em `reader.service.ts` usam paths COMPLETOS
 *   (`/api/v1/...`, `/api/r/...`). Por isso `baseURL` é só ORIGIN.
 * - Normalização defensiva: se `VITE_API_URL` vier com `/api` no final,
 *   removemos para evitar duplicação `/api/api/v1/...` (bug Sprint 1).
 */
const rawApiUrl = (import.meta.env.VITE_API_URL || '').trim();
const resolvedApiBaseUrl = rawApiUrl
  .replace(/\/+$/, '')      // remove trailing slash(es)
  .replace(/\/api$/, '');   // remove `/api` final (calls já usam path completo)

export const api: AxiosInstance = axios.create({
  baseURL: resolvedApiBaseUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

const AUTH_TOKEN_KEY = 'nextpay_reader_token';
/**
 * Chave antiga (marca anterior). Renomear sem migrar deslogaria TODO leitor
 * que ja esta com sessao aberta: o token continuaria no localStorage, so que
 * sob um nome que o codigo novo nao procura mais. Trocar nome de marca nao
 * pode custar a sessao de ninguem.
 */
const AUTH_TOKEN_KEY_LEGADO = 'imperium_reader_token';

export function getAuthToken(): string | null {
  if (typeof window === 'undefined') return null;
  const atual = window.localStorage.getItem(AUTH_TOKEN_KEY);
  if (atual) return atual;

  // Migracao silenciosa e unica: acha o token antigo, regrava sob o nome novo
  // e limpa o antigo. O leitor nem percebe.
  const legado = window.localStorage.getItem(AUTH_TOKEN_KEY_LEGADO);
  if (legado) {
    window.localStorage.setItem(AUTH_TOKEN_KEY, legado);
    window.localStorage.removeItem(AUTH_TOKEN_KEY_LEGADO);
    return legado;
  }
  return null;
}

export function setAuthToken(token: string): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(AUTH_TOKEN_KEY, token);
}

export function clearAuthToken(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(AUTH_TOKEN_KEY);
  window.localStorage.removeItem(AUTH_TOKEN_KEY_LEGADO);
}

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor de resposta: 401 → invalida sessão local + redirect login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Importação dinâmica para evitar circular dependency com store
      import('@/stores/auth').then(({ useAuthStore }) => {
        const auth = useAuthStore();
        auth.clearSession();
      });

      // Redirect só se não estamos na tela de login (evita loop)
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
      }
    }
    return Promise.reject(error);
  }
);
